/**
 * Le temps passé sur un exercice, mesuré sans mettre l'enfant en accusation.
 *
 * Demandé par Hedi le 30 août 2026, pour l'écran d'effort du tableau de bord
 * parent (`web-pwa` §6.4). La colonne `duration_ms` existait en base depuis le
 * début et **personne ne l'écrivait** : la coquille ne l'a jamais envoyée, si
 * bien que « le travail de la semaine » affichait un tiret là où il annonçait
 * une durée.
 *
 * Trois décisions, et elles tiennent toutes au même point : une durée fausse est
 * pire qu'une durée absente, parce qu'un parent la croira.
 *
 * 1. **L'horloge s'arrête quand l'écran passe en arrière-plan.** Une tablette
 *    posée pendant le dîner ajouterait quarante minutes à un exercice de deux,
 *    et c'est exactement le chiffre qu'un parent lirait comme de la lenteur. Le
 *    contrat interdit de mettre la lenteur en accusation ; la mesure doit donc
 *    refuser de l'inventer.
 * 2. **On compte depuis le dernier envoi, pas depuis l'ouverture.** Un exercice
 *    repris après une réponse fausse produit deux durées qui s'additionnent, et
 *    le total reste le temps réellement passé — là où deux mesures parties de
 *    l'ouverture compteraient le premier essai deux fois.
 * 3. **`performance.now()`, jamais `Date.now()`.** Une horloge murale qui se
 *    resynchronise pendant une séance peut reculer ; un compteur monotone ne le
 *    peut pas, et une durée négative n'a aucune façon de se rattraper en aval.
 */

/** Ce que le panneau garde entre deux rendus. Aucun état React : rien n'affiche
 *  cette valeur, et la faire vivre dans `useState` provoquerait un rendu par
 *  seconde pour un chiffre que personne ne regarde. */
export type Chrono = {
  /** L'instant du dernier départ, ou `null` tant que l'écran est caché. */
  depuis: number | null;
  /** Ce qui est déjà acquis, écran visible, depuis le dernier envoi. */
  acquis: number;
};

export function demarrer(maintenant: number): Chrono {
  return { depuis: maintenant, acquis: 0 };
}

/** L'écran devient visible ou caché. Idempotent : deux « caché » de suite ne
 *  comptent pas deux fois, et deux « visible » ne redémarrent pas deux fois. */
export function visibilite(chrono: Chrono, visible: boolean, maintenant: number): Chrono {
  if (visible) return chrono.depuis === null ? { ...chrono, depuis: maintenant } : chrono;
  if (chrono.depuis === null) return chrono;
  return { depuis: null, acquis: chrono.acquis + (maintenant - chrono.depuis) };
}

/** La durée à envoyer, et le compteur remis à zéro pour l'essai suivant. */
export function releve(chrono: Chrono, maintenant: number): { ms: number; suivant: Chrono } {
  const ecoule = chrono.acquis + (chrono.depuis === null ? 0 : maintenant - chrono.depuis);
  return {
    // Arrondi vers le bas et jamais négatif : la base n'accepte pas de durée
    // négative, et un `performance.now()` qui reculerait — il ne devrait pas —
    // ne doit pas faire échouer un envoi de réponse.
    ms: Math.max(0, Math.floor(ecoule)),
    suivant: { depuis: chrono.depuis === null ? null : maintenant, acquis: 0 },
  };
}

/**
 * Au-delà, la mesure ne veut plus rien dire et on préfère ne rien dire.
 *
 * Deux heures sur un exercice de cinq minutes ne décrit pas un enfant lent :
 * ça décrit un onglet resté ouvert, une tablette qui n'a jamais émis
 * d'événement de visibilité, ou une séance reprise le lendemain. Le seuil est
 * volontairement large — il coupe l'absurde, pas le long.
 */
export const DUREE_MAX_MS = 2 * 60 * 60 * 1000;

/** La valeur qui part avec la réponse : un nombre, ou rien. */
export function aEnvoyer(ms: number): number | undefined {
  return ms > 0 && ms <= DUREE_MAX_MS ? ms : undefined;
}
